import {
    ACTIVE_LOW_OIL_ALARM,
    ACTIVE_PRESSURE_1_ERROR,
    ACTIVE_PRESSURE_2_ERROR,
    ACTIVE_PRESSURE_ALARM,
    ACTIVE_REDUCED_DOSAGE,
    ACTIVE_SERVICE,
    ACTIVE_STATUS,
    ACTIVE_TIME_MODE,
    HIGH_PEAK_PUMP_TIME_WAIT,
    HIGH_PEAK_PUMP_TIME_WORK,
    INDICATION_ACTIVITY,
    // INDICATION_HIGHER_DOSAGE,
    // INDICATION_LOWER_DOSAGE,
    INDICATION_PUMP,
    INDICATION_SENSOR_MODE,
    INDICATION_SERVICE,
    INDICATION_TIME_MODE,
    OFF_PEAK_PUMP_TIME_WAIT,
    OFF_PEAK_PUMP_TIME_WORK,
    OIL_LEVEL,
    PASSAGE_COUNTER,
    PUMP_EVERY_N_PASSAGE,
    PUMP_PASSAGE_DELAY,
    SENSOR_MODE_PUMP_TIME_WORK,
    TIMED_MODE_PUMP_TIME_WAIT,
    TIMED_MODE_PUMP_TIME_WORK,
    TIMED_MODE_TIMED_MODE_WEEKEND_PUMP_TIME_OFF,
    TIMED_MODE_TIMED_MODE_WEEKEND_PUMP_TIME_ON,
    TIMED_MODE_WORKDAY_PUMP_TIME_OFF,
    TIMED_MODE_WORKDAY_PUMP_TIME_ON,
    VOLTAGE,
    VOLTAGE_ALARM,
} from '../../../components/shared/device-addresses'

export interface DeviceDataItem {
    address: string
    hint: string
    data: string
    type?: string
}

export interface DeviceEntity {
    uid?: string
    name?: string
    ipAddress?: string
    isMonitored?: boolean
    lastSeen?: string | number
    lastSeenStatus?: string
    latencyMs?: string | number
    oilLevelPercentage?: number
    voltage?: number
    oilAlarm?: boolean
    statusUpdating?: boolean
    description?: string
    logoWebPort?: string
    marker?: { lat: number; lng: number }
    readData?: DeviceDataItem[]
    [key: string]: unknown
}

export interface ParsedDeviceData {
    dataAlarms: DeviceDataItem[]
    dataInfo: DeviceDataItem[]
    dataSettings: DeviceDataItem[]
    dataOthers: DeviceDataItem[]
}

/**
 * Parse device data into separate arrays
 *
 * @param device - Device entity (may be null / undefined)
 * @return Object with four categorised data arrays
 */
export const parseDeviceData = (device: DeviceEntity | null | undefined): ParsedDeviceData => {
    const dataAlarms: DeviceDataItem[] = []
    const dataInfo: DeviceDataItem[] = []
    const dataSettings: DeviceDataItem[] = []
    const dataOthers: DeviceDataItem[] = []

    device?.readData
        ?.forEach(deviceData => {
            const hint = deviceData.hint

            switch (hint) {
                case ACTIVE_LOW_OIL_ALARM:
                case ACTIVE_PRESSURE_1_ERROR:
                case ACTIVE_PRESSURE_2_ERROR:
                case ACTIVE_PRESSURE_ALARM:
                case VOLTAGE_ALARM:
                    dataAlarms.push(deviceData)
                    break

                // Opened cabinet door (service / lubricant refill) is informational, not an alarm
                case ACTIVE_SERVICE:
                case VOLTAGE:
                case ACTIVE_STATUS:
                case ACTIVE_TIME_MODE:
                case ACTIVE_REDUCED_DOSAGE:
                case INDICATION_ACTIVITY:
                case INDICATION_SENSOR_MODE:
                case INDICATION_TIME_MODE:
                case INDICATION_PUMP:
                case INDICATION_SERVICE:
                case PASSAGE_COUNTER:
                case OIL_LEVEL:
                    dataInfo.push(deviceData)
                    break

                case TIMED_MODE_PUMP_TIME_WORK:
                case TIMED_MODE_PUMP_TIME_WAIT:
                case SENSOR_MODE_PUMP_TIME_WORK:
                case OFF_PEAK_PUMP_TIME_WORK:
                case OFF_PEAK_PUMP_TIME_WAIT:
                case HIGH_PEAK_PUMP_TIME_WORK:
                case HIGH_PEAK_PUMP_TIME_WAIT:
                case PUMP_EVERY_N_PASSAGE:
                case PUMP_PASSAGE_DELAY:
                    dataSettings.push(deviceData)
                    break

                case TIMED_MODE_WORKDAY_PUMP_TIME_ON:
                case TIMED_MODE_WORKDAY_PUMP_TIME_OFF:
                case TIMED_MODE_TIMED_MODE_WEEKEND_PUMP_TIME_ON:
                case TIMED_MODE_TIMED_MODE_WEEKEND_PUMP_TIME_OFF:
                    dataSettings.push({ ...deviceData, type: 'time' })
                    break

                default:
                    dataOthers.push(deviceData)
            }
        })

    return {
        dataAlarms,
        dataInfo,
        dataSettings,
        dataOthers,
    }
}

/**
 * Check if device alarms are active
 *
 * @param device - Device entity (may be null / undefined)
 * @return true when at least one alarm data value is not '0'
 */
export const alarmsActive = (device: DeviceEntity | null | undefined): boolean => {
    const { dataAlarms } = parseDeviceData(device)

    return dataAlarms?.some(dataAlarm => dataAlarm.data !== '0') ?? false
}

/**
 * Check if a service is in progress on the device (cabinet door switch is open)
 *
 * The service state is informational only, so it is intentionally not part of `alarmsActive`.
 *
 * @param device - Device entity (may be null / undefined)
 * @return true when the ACTIVE_SERVICE data value is not '0'
 */
export const isServiceInProgress = (device: DeviceEntity | null | undefined): boolean => {
    const serviceData = device?.readData?.find(deviceData => deviceData.hint === ACTIVE_SERVICE)?.data

    return serviceData !== undefined && Number.parseInt(serviceData, 10) !== 0
}
